import type { ProjectMessage } from "@/domains/messages/types";

/** Consecutive messages from the same author within this window collapse
 *  under one avatar/name header. */
const GROUPING_WINDOW_MS = 5 * 60 * 1000;

/** How long a deleted message's tombstone stays visible after deletion. */
export const TOMBSTONE_TTL_MS = 30 * 60 * 1000;

/**
 * Drop deleted messages whose tombstone has outlived TOMBSTONE_TTL_MS. Run
 * before grouping so a day whose only message expired loses its divider too.
 * Pure — `now` is injected for testability and for the live re-render tick.
 */
export function filterExpiredTombstones(messages: ProjectMessage[], now: number): ProjectMessage[] {
  return messages.filter(
    (m) => !m.deleted_at || now - new Date(m.deleted_at).getTime() < TOMBSTONE_TTL_MS
  );
}

export interface RenderableMessage {
  message: ProjectMessage;
  /** When false, the row hides the avatar + author/timestamp header and aligns
   *  under the previous message from the same author. */
  showHeader: boolean;
}

export interface DateGroup {
  /** Day key (YYYY-MM-DD, local) — used as a stable React key. */
  dayKey: string;
  /** The ISO timestamp of the first message that day, for divider labelling. */
  dayTimestamp: string;
  items: RenderableMessage[];
}

function dayKeyOf(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Group an ascending (oldest-first) message list into per-day buckets, marking
 * which messages start a new author-run (showHeader) vs continue one. A new
 * header is forced when the author changes, the day changes, the gap exceeds
 * the grouping window, or the message is a reply (so its quoted parent reads
 * clearly).
 *
 * Pure + side-effect free so it can be unit-tested without React.
 */
export function groupMessages(messages: ProjectMessage[]): DateGroup[] {
  const groups: DateGroup[] = [];
  let currentGroup: DateGroup | null = null;
  let prev: ProjectMessage | null = null;

  for (const message of messages) {
    const dayKey = dayKeyOf(message.created_at);

    if (!currentGroup || currentGroup.dayKey !== dayKey) {
      currentGroup = { dayKey, dayTimestamp: message.created_at, items: [] };
      groups.push(currentGroup);
      prev = null; // a new day always starts a fresh author run
    }

    const sameAuthor = prev != null && prev.author_id === message.author_id;
    const withinWindow =
      prev != null &&
      new Date(message.created_at).getTime() - new Date(prev.created_at).getTime() <=
        GROUPING_WINDOW_MS;
    const showHeader = !(sameAuthor && withinWindow) || message.reply_to_id != null;

    currentGroup.items.push({ message, showHeader });
    prev = message;
  }

  return groups;
}
